import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { accountService } from '../../../services/accountService';

const EditGoalModal = ({ goal, onClose, onUpdate }) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [formData, setFormData] = useState({
    goalName: goal?.goalName,
    targetAmount: goal?.targetAmount,
    deadline: goal?.deadline || '',
    status: goal?.status,
    linkedAccountId: goal?.linkedAccountId || '',
    autoCloseOnCompletion: goal?.autoCloseOnCompletion || false,
    notes: goal?.notes || ''
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const data = await accountService?.getAll();
      setAccounts(data || []);
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    if (!formData?.goalName?.trim()) {
      setError('Please enter a goal name');
      setLoading(false);
      return;
    }

    // For debt payoff, allow 0 as target (pay off all debt)
    // For savings, require positive target amount
    if (goal?.goalType === 'savings_target' && (!formData?.targetAmount || parseFloat(formData?.targetAmount) <= 0)) {
      setError('Please enter a valid target amount');
      setLoading(false);
      return;
    }

    if (goal?.goalType === 'debt_payoff' && (formData?.targetAmount === '' || parseFloat(formData?.targetAmount) < 0)) {
      setError('Target amount must be 0 or greater for debt payoff');
      setLoading(false);
      return;
    }

    try {
      await onUpdate(goal?.id, {
        goalName: formData?.goalName,
        targetAmount: parseFloat(formData?.targetAmount),
        deadline: formData?.deadline || null,
        status: formData?.status,
        linkedAccountId: formData?.linkedAccountId || null,
        autoCloseOnCompletion: formData?.autoCloseOnCompletion,
        notes: formData?.notes
      });
    } catch (err) {
      setError(err?.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-24 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-card-foreground">Edit Goal</h2>
            <p className="text-sm text-muted-foreground mt-4">
              {goal?.goalType === 'savings_target' ? 'Savings Target' : 'Debt Payoff'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-8 hover:bg-muted rounded-lg transition-smooth min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-24">
          {error && (
            <div className="mb-16 p-12 bg-destructive/10 border border-destructive rounded-lg">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <div className="mb-20">
            <label className="block text-sm font-medium text-foreground mb-8">
              Goal Name *
            </label>
            <input
              type="text"
              value={formData?.goalName}
              onChange={(e) => setFormData({ ...formData, goalName: e?.target?.value })}
              className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
              required
            />
          </div>

          <div className="mb-20">
            <label className="block text-sm font-medium text-foreground mb-8">
              Target Amount *
            </label>
            <div className="relative">
              <span className="absolute left-16 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData?.targetAmount}
                onChange={(e) => setFormData({ ...formData, targetAmount: e?.target?.value })}
                className="w-full pl-32 pr-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
                required
              />
            </div>
          </div>

          <div className="mb-20">
            <label className="block text-sm font-medium text-foreground mb-8">
              Deadline
            </label>
            <input
              type="date"
              value={formData?.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e?.target?.value })}
              className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
            />
          </div>

          <div className="mb-20">
            <label className="block text-sm font-medium text-foreground mb-8">
              Link to Account
            </label>
            <select
              value={formData?.linkedAccountId}
              onChange={(e) => setFormData({ ...formData, linkedAccountId: e?.target?.value })}
              className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
            >
              <option value="">No Account Link</option>
              {accounts?.map((account) => (
                <option key={account?.id} value={account?.id}>
                  {account?.name} ({account?.group}) - ${account?.balance?.toFixed(2)}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-4">
              Link this goal to an account to automatically track progress based on account balance
            </p>
          </div>

          <div className="mb-20">
            <label className="block text-sm font-medium text-foreground mb-8">
              Status
            </label>
            <select
              value={formData?.status}
              onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
              className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px]"
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="mb-24">
            <label className="block text-sm font-medium text-foreground mb-8">
              Notes
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
              className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[80px] resize-none"
              rows={3}
            />
          </div>

          <div className="mb-24">
            <label className="flex items-center gap-12 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.autoCloseOnCompletion}
                onChange={(e) => setFormData({ ...formData, autoCloseOnCompletion: e?.target?.checked })}
                className="w-18 h-18 rounded border-input text-primary focus:ring-2 focus:ring-primary cursor-pointer"
              />
              <div>
                <span className="text-sm font-medium text-foreground">Auto-close when completed</span>
                <p className="text-xs text-muted-foreground mt-2">
                  Automatically mark this goal as completed when the target amount is reached
                </p>
              </div>
            </label>
          </div>

          <div className="flex gap-12">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-20 py-12 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth font-medium min-h-[44px] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-20 py-12 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium min-h-[44px] disabled:opacity-50 flex items-center justify-center gap-8"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditGoalModal;